import React, { useCallback, useContext } from "react";
import { Icons } from "../Icons";
import { DropdownContext } from "./Dropdown";

/**
 * DropdownItem — Individual selectable item within a DropdownMenu.
 *
 * Supports:
 * - Optional leading icon
 * - Disabled state (grays out, blocks click and keyboard interaction)
 * - Keyboard navigation (Enter and Space trigger the action)
 * - ARIA attributes for accessibility (role="menuitem", aria-disabled)
 *
 * @param {string}  IconName       - Optional icon shown before the label
 * @param {func}    onClick        - Action to perform when selected
 * @param {string}  customClasses  - E.g. 'dropdown-item-danger' for destructive actions
 * @param {boolean} disabled       - Disables the item
 */
export const DropdownItem = ({
  IconName,
  children,
  onClick,
  fontClass = "body-m",
  customClasses = "",
  customStyle = {},
  disabled = false,
  selected = false,
  ...props
}) => {
  const dropdownCtx = useContext(DropdownContext);
  const close = dropdownCtx?.close;

  /** Click handler — blocks interaction when disabled */
  const handleClick = useCallback((e) => {
    if (disabled) return;
    onClick?.(e);
    close?.();
  }, [disabled, onClick, close]);

  /** Keyboard handler — Enter and Space act as click for accessibility */
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault(); // Prevent page scroll on Space
      onClick?.(e);
      close?.();
    }
  }, [disabled, onClick, close]);

  return (
    <div
      {...props}
      className={`dropdown-item ${fontClass} ${disabled ? "dropdown-item--disabled" : ""} ${selected ? "dropdown-item--selected" : ""} ${customClasses}`}
      style={customStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      {IconName && <span className="dropdown-item-icon"><Icons name={IconName} size={16} /></span>}

      <span className="dropdown-item-text">
        {children}
      </span>

      {selected && (
        <span className="dropdown-item-icon dropdown-item-icon--trailing">
          <Icons name="check" size={16} />
        </span>
      )}
    </div>
  );
};
